import { MelodyPlayer } from "../audio/MelodyPlayer.js";
import { MelodyContext } from "../control/MelodyContext.js";
import { VOMelodyPairItem } from "../model/DataStructure.js";
import { Labels } from "../model/Labels.js";
import { Confetti } from "./Confetti.js";
import { Counter } from "./Counter.js";
import { Decoration } from "./Decoration.js";
import { MelodyInstrumentHeader } from "./MelodyInstrumentHeader.js";
import { MelodyProgress } from "./MelodyProgress.js";
import { ProgressButton } from "./ProgressButton.js";
import { VisualInstrument } from "./VisualInstrument.js";

export interface MelodyInstrumentOptions {
    container: HTMLElement;
    item?: VOMelodyPairItem;
    labels: Labels;
    confetti: Confetti;
    debug: boolean;
    showCurly: boolean;
    showDashes: boolean;
}

export class MelodyInstrument {

    public readonly buttonA: ProgressButton;
    public readonly buttonB: ProgressButton;
    public readonly buttonC: ProgressButton;

    private _options: MelodyInstrumentOptions;
    private _element: HTMLDivElement;
    private _player: MelodyPlayer;
    private _decoration: Decoration;
    private _visual: VisualInstrument;
    private _progress: MelodyProgress;
    private _counter: Counter;

    private _title: HTMLElement;
    private _subtitle: HTMLElement;
    private _info: HTMLElement;
    private _item: VOMelodyPairItem = null;
    private _header: MelodyInstrumentHeader;



    constructor(options: MelodyInstrumentOptions) {
        this._options = e5.object.merge({

        }, options);

        this._element = document.createElement("div");
        this._element.className = "melody-instrument melody-instrument--hidden";
        this._element.innerHTML = `
        <div class="melody-instrument__decoration"></div>

        <div class="melody-instrument__header"></div>

        <div class="melody-instrument__content">
            <div class="melody-instrument__visual"></div>
            <div class="melody-instrument__footer"></div>
        </div>

        <div class="melody-instrument__text">
            <div class="melody-instrument__title melody-instrument__text--hidden"></div>
            <div class="melody-instrument__subtitle melody-instrument__text--hidden"></div>
            <div class="melody-instrument__info melody-instrument__text--hidden"></div>
            <div class="melody-instrument__foreplay-buttons melody-instrument__foreplay-buttons--hidden"></div>
        </div>

        <div class="melody-instrument__progress e5-hidden">
            <div class="melody-instrument__progress-track"></div>
        </div>
        `;

        this._title = this._element.querySelector(".melody-instrument__title");
        this._subtitle = this._element.querySelector(".melody-instrument__subtitle");
        this._info = this._element.querySelector(".melody-instrument__info");

        this._progress = new MelodyProgress({
            container: this._element.querySelector(".melody-instrument__footer"),
            visible: false,
            showCurly: options.showCurly
        });

        this._decoration = new Decoration({
            container: this._element.querySelector(".melody-instrument__decoration"),
            names: [
                "melody-instrument-1",
                "melody-instrument-6"
            ]
        });

        const buttons: ProgressButton[] = [];
        const createBtn = () => {
            const btn = new ProgressButton({
                // label: options.labels.text("melody.melody_instrument.foreplay.button.repeat"),
                container: this._element.querySelector(".melody-instrument__foreplay-buttons"),
                // style: "outline-small",
                // fontSize: "tiny",
                visible: false
            });
            buttons.push(btn);
            btn.onVisibilityChange.add(
                () => {
                    this._element.querySelector(".melody-instrument__foreplay-buttons")
                        .classList.toggle("melody-instrument__foreplay-buttons--hidden", !buttons.find((b) => b.visible));
                }
            );
            return btn;
        }
        this.buttonA = createBtn();
        this.buttonB = createBtn();
        this.buttonC = createBtn();

        this._counter = new Counter({
            container: this._element.querySelector(".melody-instrument__footer"),
        });

        this._visual = new VisualInstrument({
            container: this._element.querySelector(".melody-instrument__visual"),
            visible: false,
            clickable: false,
            showDashes: options.showDashes
        });

        this._header = new MelodyInstrumentHeader({
            container: this._element.querySelector(".melody-instrument__header"),
            labels: this._options.labels
        });

        this._player = new MelodyPlayer({
            visual: this._visual
        });

        if (options.item) {
            this.item = options.item;
        }

        if (options.debug) {
            var progress = this._element.querySelector(".melody-instrument__progress") as HTMLElement;
            progress.classList.remove("e5-hidden");
            progress.addEventListener("pointerup", (e) => {
                const local = e5.Transform.toLocal(progress, e.clientX, e.clientY);
                this._player.time = (local.x / progress.offsetWidth) * this._player.duration;
            })
            var track = this._element.querySelector(".melody-instrument__progress-track") as HTMLElement;
            this._player.onTime.add((e) => {
                if (!e.duration) {
                    track.style.width = "0";
                } else {
                    track.style.width = ((e.time / e.duration) * 100).toFixed(6) + "%";
                }
            });
        }

        if (options.container) {
            options.container.appendChild(this._element);
        }
    }

    public get item(): VOMelodyPairItem {
        return this._item;
    }

    public set item(value: VOMelodyPairItem) {
        if (this._item === value) {
            return;
        }
        this._item = value;
        this._player.virtual = value?.instrument?.virtual;
        this._visual.instrument = value?.instrument;
        // this._header.title = value?.instrument.name || "";

        this._element.style.setProperty("--accent-color", value?.instrument ? value.instrument?.color : "#666");
    }

    public get title(): string {
        return this._title.textContent;
    }

    public set title(value: string) {
        this._title.classList.toggle("melody-instrument__text--hidden", !Boolean(value));
        if (value) {
            this._title.textContent = value;
        }
    }

    public get subtitle(): string {
        return this._subtitle.textContent;
    }

    public set subtitle(value: string) {
        this._subtitle.classList.toggle("melody-instrument__text--hidden", !Boolean(value));
        if (value) {
            this._subtitle.textContent = value;
        }
    }

    public get info(): string {
        return this._info.textContent;
    }

    public set info(value: string) {
        this._info.classList.toggle("melody-instrument__text--hidden", !Boolean(value));
        if (value) {
            this._info.textContent = value;
        }
    }

    public fillContext(ctx: MelodyContext): MelodyContext {
        ctx.player = this._player;
        ctx.visual = this._visual;
        ctx.progress = this._progress;
        ctx.counter = this._counter
        ctx.labels = this._options.labels;
        ctx.confetti = this._options.confetti;
        ctx.instrumentHeader = this._header;
        return ctx;
    }

    public get visible(): boolean {
        return !this._element.classList.contains("melody-instrument--hidden");
    }

    public set visible(value: boolean) {
        if (this.visible === value) {
            return;
        }

        this._element.classList.toggle("melody-instrument--hidden", !value);
        this._decoration.visible = value;
    }
}